import React from 'react';
import { Delete, Edit } from '@mui/icons-material';
import {
	SchedulerPosition,
	deletePosition,
	JobCode,
} from '../../../../core/API/scheduler';
import { getPositionJobCodeId, POSITION_DELETE_BLOCKED_MESSAGE } from '../../../../module/schedule/utils/scheduleDepartmentUtils';
import styles from '../SchedulerSetting.module.scss';

interface PositionsListProps {
	positions: SchedulerPosition[];
	jobCodes: JobCode[];
	onPositionChanged: () => void;
	onEditClick: (position: SchedulerPosition) => void;
	canEdit?: boolean;
	canDelete?: boolean;
}

const getJobCodeForPosition = (
	position: SchedulerPosition,
	jobCodes: JobCode[]
): JobCode | null => {
	if (position.jobCode && typeof position.jobCode !== 'string') {
		return position.jobCode;
	}
	const jobCodeId = getPositionJobCodeId(position);
	if (!jobCodeId) return null;
	return jobCodes.find((jc) => jc.id === jobCodeId) || null;
};

const getJobCodeLabel = (position: SchedulerPosition, jobCodes: JobCode[]): string =>
	getJobCodeForPosition(position, jobCodes)?.label || '-';

const comparePositionsByJobCodeThenName = (
	a: SchedulerPosition,
	b: SchedulerPosition,
	jobCodes: JobCode[]
): number => {
	const aJob = getJobCodeForPosition(a, jobCodes);
	const bJob = getJobCodeForPosition(b, jobCodes);
	const aNum = aJob?.jobNumber ?? Infinity;
	const bNum = bJob?.jobNumber ?? Infinity;
	if (aNum !== bNum) return aNum - bNum;

	const labelCompare = (aJob?.label || '').localeCompare(bJob?.label || '');
	if (labelCompare !== 0) return labelCompare;

	return a.name.localeCompare(b.name);
};

const PositionsList: React.FC<PositionsListProps> = ({
	positions,
	jobCodes,
	onPositionChanged,
	onEditClick,
	canEdit = true,
	canDelete = true,
}) => {
	const handleDeleteClick = async (position: SchedulerPosition) => {
		if (!canDelete) return;

		const ok = window.confirm(
			`Are you sure you want to delete position "${position.name}"? This action cannot be undone.`
		);
		if (!ok) return;

		try {
			await deletePosition(position.id);
			onPositionChanged();
		} catch (e: any) {
			const message =
				e?.response?.data?.['hydra:description'] ||
				e?.response?.data?.message ||
				e?.message ||
				'Failed to delete position';
			window.alert(
				message.toLowerCase().includes('remove positions from schedule')
					? POSITION_DELETE_BLOCKED_MESSAGE
					: message
			);
		}
	};

	const sortedPositions = [...positions].sort((a, b) =>
		comparePositionsByJobCodeThenName(a, b, jobCodes)
	);

	return (
		<div className={styles.tableContainer}>
			<table className={styles.workplaceTable}>
				<thead>
					<tr>
						<th>Name</th>
						<th>Job Code</th>
						<th style={{ width: 48, textAlign: 'center' }}>Edit</th>
						<th style={{ width: 48, textAlign: 'center' }}>Delete</th>
					</tr>
				</thead>
				<tbody>
					{sortedPositions.length === 0 ? (
						<tr>
							<td colSpan={4}>
								<span>No positions configured</span>
							</td>
						</tr>
					) : (
						sortedPositions.map((position) => (
							<tr key={position.id}>
								<td>
									<span>{position.name}</span>
								</td>
								<td>
									<span>{getJobCodeLabel(position, jobCodes)}</span>
								</td>
								<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
									<button
										className={styles.iconButton}
										title={canEdit ? 'Edit' : 'You do not have permission to edit'}
										onClick={() => onEditClick(position)}
										disabled={!canEdit}
										style={{
											opacity: canEdit ? 1 : 0.5,
											cursor: canEdit ? 'pointer' : 'not-allowed',
										}}
									>
										<Edit className={styles.editIcon} />
									</button>
								</td>
								<td style={{ textAlign: 'center', padding: '8px', width: 48 }}>
									<button
										onClick={() => handleDeleteClick(position)}
										title={canDelete ? 'Delete Position' : 'You do not have permission to delete'}
										className={styles.iconButton}
										disabled={!canDelete}
										style={{
											opacity: canDelete ? 1 : 0.5,
											cursor: canDelete ? 'pointer' : 'not-allowed',
										}}
									>
										<Delete className={styles.deleteIcon} />
									</button>
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
		</div>
	);
};

export default PositionsList;
